import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, FileText } from "lucide-react";
import { navLinks, profil, cvList } from "../../data/content";
import { CVMenu, trackCvDownload } from "./CVMenu";

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -20 });
  } else {
    el.scrollIntoView({ behavior: "smooth" });
  }
};

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("accueil");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scrollspy — surligne l'onglet de la section visible (#axes rattaché à Profil)
  useEffect(() => {
    const spyMap = { axes: "profil" };
    const ids = ["accueil", "profil", "axes", "competences", "experiences", "parcours", "contact"];
    const sections = ids.map((id) => document.getElementById(id)).filter(Boolean);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            setActive(spyMap[id] || id);
          }
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), 10);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 1.4, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${scrolled ? "glass border-b border-white/5" : "bg-transparent"}`}
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
        <button onClick={() => go("accueil")} className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <img
            src="/andrew.png"
            alt="Andrew Picornell"
            className="h-9 w-9 rounded-full object-cover object-top border border-white/20 group-hover:border-cyan/70 transition-colors duration-300"
          />
          <span className="font-display font-semibold text-sm tracking-tight text-white/90 hidden sm:block">Andrew Picornell</span>
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {navLinks.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className={`nav-underline text-sm transition-colors duration-300 font-body ${active === l.href ? "text-cyan" : "text-[var(--text-2)] hover:text-white"}`}
              data-testid={`nav-link-${l.href}`}
              data-active={active === l.href}
              aria-current={active === l.href ? "true" : undefined}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden sm:block">
            <CVMenu variant="nav" label="Télécharger mon CV" testid="nav-cv" align="right" />
          </div>
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden text-white p-2"
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="lg:hidden overflow-hidden glass border-t border-white/5"
          >
            <div className="px-6 py-5 flex flex-col gap-1">
              {navLinks.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className={`text-left py-3 text-lg font-display border-b border-white/5 transition-colors ${active === l.href ? "text-cyan" : "text-white/90"}`}
                  data-testid={`nav-mobile-link-${l.href}`}
                  data-active={active === l.href}
                >
                  {l.label}
                </button>
              ))}
              <div className="mt-4 space-y-1.5">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--muted)] px-1 mb-1">Télécharger mon CV</p>
                {cvList.map((cv, i) => (
                  <a
                    key={i}
                    href={cv.file}
                    download={cv.download}
                    onClick={() => { trackCvDownload(cv.slug); setOpen(false); }}
                    className="flex items-center gap-2.5 text-sm text-white/90 bg-white/5 rounded-lg px-3 py-2.5"
                    data-testid={`nav-mobile-cv-${i}`}
                  >
                    <FileText size={15} className="text-cyan shrink-0" /> {cv.label}
                  </a>
                ))}
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
